import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { MarketCode } from '@schuettflix/utils';
import { getMarketsFromToken } from '@/shared/utils/getMarketsFromToken.ts';
import { decodeJwt, JwtPayload } from '@/shared/utils/decodeJwt.ts';

export type AuthTokenContextType = {
    initialize: (token: string) => void;
    isInitialized: () => boolean;
    isMarketAvailable: (marketCode: MarketCode) => boolean;
    getToken: () => string;
    getTokenPayload: () => JwtPayload;
    getDefaultMarketCode: () => MarketCode;
    getAvailableMarketCodes: () => MarketCode[];
};

export const AuthTokenContext = createContext<AuthTokenContextType | undefined>(undefined);

export function AuthTokenContextProvider({ children }: PropsWithChildren) {
    const [token, setToken] = useState<string | undefined>(undefined);
    const [availableMarketCodes, setAvailableMarketCodes] = useState<MarketCode[] | undefined>(undefined);

    const initialize = useCallback((token: string) => {
        const availableMarketCodes = getMarketsFromToken(token);

        if (availableMarketCodes.length === 0) {
            throw new Error(`Authenticated user has no access to any market`);
        }

        setToken(token);
        setAvailableMarketCodes(availableMarketCodes);
    }, []);

    const isInitialized = useCallback(
        () => !(token === undefined || availableMarketCodes === undefined),
        [token, availableMarketCodes]
    );

    const getToken = useCallback(() => {
        if (token === undefined) {
            throw new Error(`Context needs to be initialized via \`initialize(token)\``);
        }
        return token;
    }, [token]);

    const getDefaultMarketCode = useCallback(() => {
        if (availableMarketCodes === undefined) {
            throw new Error(`Context needs to be initialized via \`initialize(token)\``);
        }
        return availableMarketCodes[0];
    }, [availableMarketCodes]);

    const getAvailableMarketCodes = useCallback(() => {
        if (availableMarketCodes === undefined) {
            throw new Error(`Context needs to be initialized via \`initialize(token)\``);
        }
        return availableMarketCodes;
    }, [availableMarketCodes]);

    const getTokenPayload = useCallback(() => decodeJwt(getToken()), [getToken]);

    const isMarketAvailable = useCallback(
        (marketCode: MarketCode) => getAvailableMarketCodes().includes(marketCode),
        [getAvailableMarketCodes]
    );

    return (
        <AuthTokenContext.Provider
            value={{
                initialize,
                isInitialized,
                isMarketAvailable,
                getToken,
                getTokenPayload,
                getDefaultMarketCode,
                getAvailableMarketCodes,
            }}
        >
            {children}
        </AuthTokenContext.Provider>
    );
}

export function useAuthTokenContext() {
    const context = useContext(AuthTokenContext);
    if (!context) {
        throw new Error('AuthTokenContext must be provided!');
    }
    return context;
}
